import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth, roleHome } from "@/context/AuthContext";
import { Field } from "@/components/common";
import BackNav from "@/components/BackNav";
import WhatsAppLink from "@/components/WhatsAppLink";

export default function Accedi() {
  const [mode, setMode] = useState("login");
  const [proMode, setProMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", surname: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const { login, register, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "pro" && proMode === "register") {
        const r = await api.post("/auth/register-professional", {
          email: form.email.trim().toLowerCase(), password: form.password.trim(),
          name: form.name.trim(), surname: form.surname.trim(), phone: form.phone.trim(),
        });
        toast.success(r.data.message);
        setProMode("login");
        return;
      }
      const user =
        mode === "register"
          ? await register({ ...form, email: form.email.trim().toLowerCase(), name: form.name.trim(), surname: form.surname.trim() })
          : await login(form.email.trim().toLowerCase(), form.password.trim());
      if (mode === "pro" && user.role !== "professional") {
        logout();
        setError("Questo account non è un account professionista. Usa la scheda \"Accedi\" per entrare.");
        return;
      }
      toast.success(mode === "register" ? "Registrazione completata." : "Bentornato.");
      const from = location.state?.from;
      navigate(mode === "pro" ? "/area-pro" : from && from !== "/accedi" ? from : roleHome(user));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async () => {
    setForgotError("");
    setForgotBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
      toast.success(r.data.message);
    } catch (err) {
      setForgotError(apiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14" data-testid="auth-page">
      <div className="mb-6"><BackNav /></div>
      <div className="card-soft p-8">
        <div className="flex gap-2 mb-8 bg-[#f0f7ff] rounded-full p-1" data-testid="auth-tabs">
          {[["login", "Accedi"], ["register", "Registrati"], ["pro", "Professionista"]].map(([m, label]) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                mode === m ? "bg-white text-navy shadow-sm" : "text-muted-soft"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <h1 className="font-serif text-3xl text-navy mb-6">
          {mode === "login" ? "Bentornato." : mode === "pro" ? (proMode === "login" ? "Area Professionista." : "Diventa professionista.") : "Crea il tuo account."}
        </h1>
        {mode === "pro" && (
          <div className="flex gap-2 mb-6 -mt-2" data-testid="pro-subtabs">
            {[["login", "Accedi"], ["register", "Nuova richiesta"]].map(([m, label]) => (
              <button
                key={m}
                type="button"
                data-testid={`pro-tab-${m}`}
                onClick={() => { setProMode(m); setError(""); }}
                className={`flex-1 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                  proMode === m ? "bg-[#1e293b] text-white border-[#1e293b]" : "bg-white text-muted-soft border-[#dbe7f3]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {mode === "pro" && proMode === "register" && (
          <p className="text-sm text-muted-soft -mt-2 mb-6">L'amministratore attiverà il tuo account e ti assegnerà il Comune di riferimento in esclusiva. Non è possibile scegliere il Comune autonomamente.</p>
        )}

        <form onSubmit={submit} className="space-y-4">
          {(mode === "register" || (mode === "pro" && proMode === "register")) && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" testid="field-name">
                <input data-testid="input-name" className="input-soft" value={form.name} onChange={set("name")} required />
              </Field>
              <Field label="Cognome" testid="field-surname">
                <input data-testid="input-surname" className="input-soft" value={form.surname} onChange={set("surname")} required />
              </Field>
            </div>
          )}
          <Field label="Email" testid="field-email">
            <input data-testid="input-email" type="email" className="input-soft" value={form.email} onChange={set("email")} required />
          </Field>
          {(mode === "register" || (mode === "pro" && proMode === "register")) && (
            <Field label="Telefono" testid="field-phone">
              <input data-testid="input-phone" className="input-soft" value={form.phone} onChange={set("phone")} />
            </Field>
          )}
          <Field label="Password" testid="field-password">
            <input data-testid="input-password" type="password" className="input-soft" value={form.password} onChange={set("password")} required minLength={6} />
          </Field>
          {mode === "login" && !forgotOpen && (
            <p className="text-right -mt-2">
              <button type="button" data-testid="login-forgot-password-link"
                onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotError(""); setForgotEmail(form.email); }}
                className="text-sm font-semibold text-[#1e3a5f] hover:underline">
                Password dimenticata?
              </button>
            </p>
          )}
          {mode === "login" && forgotOpen && (
            <div className="card-ivory p-4 -mt-2 space-y-3" data-testid="forgot-password-box">
              {forgotSent ? (
                <p className="text-sm text-muted-soft" data-testid="forgot-success">Se questa email è registrata, riceverai a breve il link per reimpostare la password. Controlla anche la cartella Spam.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-soft">Inserisci l'email usata in registrazione: ti invieremo un link sicuro (valido 24 ore) per reimpostare la password.</p>
                  <input data-testid="input-forgot-email" type="email" className="input-soft" placeholder="La tua email"
                    value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                  {forgotError && <p data-testid="forgot-error" className="text-sm text-red-700">{forgotError}</p>}
                  <div className="flex items-center gap-4">
                    <button type="button" data-testid="forgot-submit" disabled={forgotBusy || !forgotEmail.trim()} onClick={submitForgot}
                      className="btn-primary flex-1 disabled:opacity-60">{forgotBusy ? "Invio in corso…" : "Invia link di recupero"}</button>
                    <button type="button" data-testid="forgot-cancel" onClick={() => setForgotOpen(false)}
                      className="text-sm text-muted-soft hover:underline">Annulla</button>
                  </div>
                </>
              )}
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{error}</p>}

          <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
            {busy ? "Un momento…" : mode === "pro" ? (proMode === "login" ? "Accedi all'Area Professionista" : "Invia richiesta di attivazione") : mode === "login" ? "Accedi" : "Registrati"}
          </button>
        </form>
      </div>

      <p className="text-center mt-5 text-sm text-muted-soft">
        <WhatsAppLink label="Hai bisogno di aiuto? Scrivici su WhatsApp" testid="login-whatsapp" />
      </p>
    </div>
  );
}
